import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Shield, Clock, DollarSign, CreditCard, TrendingUp, Users, Award, ArrowRight, Star } from 'lucide-react';

const SellGiftCardsOnline = () => {
  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Sell Gift Cards Online — Instant Payout | CashifyGCMart</title>
        <meta name="description" content="Sell your unwanted gift cards online for instant cash. Get up to 93% value with same-day payouts. Accept 230+ brands including Amazon, Visa, Walmart, Target. Safe, fast, and secure." />
        <meta name="keywords" content="sell gift cards online, sell gift cards for cash, instant gift card payout, online gift card exchange, cash for gift cards" />
        <link rel="canonical" href="https://cashifygcmart.com/sell-gift-cards-online" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Sell Gift Cards Online — Instant Payout | CashifyGCMart" />
        <meta property="og:description" content="Turn your unused gift cards into cash instantly. Up to 93% value, same-day payouts, 230+ brands accepted." />
        <meta property="og:url" content="https://cashifygcmart.com/sell-gift-cards-online" />
        <meta property="og:type" content="website" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Sell Gift Cards Online",
            "provider": {
              "@type": "Organization",
              "name": "CashifyGCMart",
              "url": "https://cashifygcmart.com"
            },
            "description": "Online gift card selling service offering instant cash for unwanted gift cards",
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Gift Card Brands We Accept",
              "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Amazon Gift Card Exchange" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Visa Gift Card Exchange" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Walmart Gift Card Exchange" } }
              ]
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Sell Your Gift Cards Online for <span className="text-pink-600">Instant Cash</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                Turn unwanted gift cards into money within minutes. Get up to 93% of card value with same-day payouts. Safe, fast, and hassle-free.
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 mb-10 text-sm md:text-base">
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium">410,811+ Happy Customers</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="font-medium">SSL Secured</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Same-Day Payout</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/form-submission">
                  <button className="w-full sm:w-auto bg-pink-600 hover:bg-pink-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl transform flex items-center justify-center gap-2">
                    Start Selling Now
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </a>
                <a href="/rate-calculator">
                  <button className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg border-2 border-gray-300 transition-all duration-300 hover:scale-105 hover:shadow-xl transform">
                    Check Your Rate
                  </button>
                </a>
              </div>

              {/* Star Rating */}
              <div className="mt-8 flex items-center justify-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-600 font-medium">4.8/5 based on 15,000+ reviews</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Sell With Us Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Sell Your Gift Cards With CashifyGCMart?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We're the trusted online gift card exchange platform making it simple to sell gift cards for cash.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-6 rounded-2xl border-2 border-pink-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Best Rates Online</h3>
                <p className="text-gray-600">
                  Get up to 93% of your gift card value — the highest rates in the industry for selling gift cards online.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border-2 border-blue-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Instant Payouts</h3>
                <p className="text-gray-600">
                  Receive your money within minutes via PayPal, Zelle, Cash App, or bank transfer. No waiting weeks for payment.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border-2 border-green-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">100% Safe & Secure</h3>
                <p className="text-gray-600">
                  SSL encrypted transactions and secure payment processing. Your personal data is always protected.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">230+ Brands Accepted</h3>
                <p className="text-gray-600">
                  Sell Amazon, Visa, Walmart, Target, Best Buy, Nike, Starbucks, and 200+ more popular gift card brands.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-2xl border-2 border-orange-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">No Hidden Fees</h3>
                <p className="text-gray-600">
                  What you see is what you get. No surprise deductions or processing fees on your gift card sale.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-2xl border-2 border-indigo-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Trusted by Thousands</h3>
                <p className="text-gray-600">
                  Over 410,000 customers have sold their gift cards online with us. Join America's #1 gift card exchange.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How to Sell Gift Cards Online in 3 Easy Steps
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Selling your unwanted gift cards for cash has never been easier. Get paid in minutes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Step 1 */}
              <div className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-600 text-white rounded-full text-2xl font-bold mb-4">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Enter Card Details</h3>
                  <p className="text-gray-600">
                    Tell us your gift card brand and balance. Upload a photo or enter the card number manually.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-600 text-white rounded-full text-2xl font-bold mb-4">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Get Instant Quote</h3>
                  <p className="text-gray-600">
                    See your offer immediately — up to 93% of card value. No waiting, no negotiation needed.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-600 text-white rounded-full text-2xl font-bold mb-4">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Receive Your Cash</h3>
                  <p className="text-gray-600">
                    Choose your payment method and get paid the same day via PayPal, Zelle, Cash App, or Bitcoin.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <a href="/form-submission">
                <button className="bg-pink-600 hover:bg-pink-700 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl transform inline-flex items-center gap-2">
                  Sell Your Gift Cards Now
                  <ArrowRight className="w-5 h-5" />
                </button>
              </a>
            </div>
          </div>
        </section>

        {/* Supported Brands Table */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Top Gift Card Brands We Buy Online
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Get the best rates when you sell popular gift cards online. We accept physical and digital gift cards.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-pink-600 to-purple-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-lg font-semibold">Gift Card Brand</th>
                    <th className="px-6 py-4 text-center text-lg font-semibold">Payout Rate</th>
                    <th className="px-6 py-4 text-center text-lg font-semibold">Payout Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Amazon Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 93%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Visa Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 90%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Walmart Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 88%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Target Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 85%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Best Buy Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 82%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Apple Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 80%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Nike Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 78%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                  <tr className="hover:bg-pink-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Starbucks Gift Card</td>
                    <td className="px-6 py-4 text-center text-pink-600 font-bold">Up to 75%</td>
                    <td className="px-6 py-4 text-center text-gray-700">Same Day</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="text-center mt-8">
              <a href="/accepted-cards" className="text-pink-600 hover:text-pink-700 font-semibold text-lg inline-flex items-center gap-2">
                View All 230+ Accepted Brands
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions About Selling Gift Cards Online
              </h2>
            </div>

            <div className="space-y-6">
              {/* FAQ 1 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Is it safe to sell gift cards online?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes, absolutely. CashifyGCMart uses bank-level SSL encryption to protect your personal and payment information. We've safely processed over 410,000 gift card transactions with a 4.8/5 customer satisfaction rating. Your gift card details and payment information are always secure.
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  How fast do I get paid when selling gift cards online?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Most payments are processed the same day, often within minutes of verification. Once we confirm your gift card details, your money is sent directly to your chosen payment method (PayPal, Zelle, Cash App, or bank transfer). There's no waiting weeks for a check in the mail.
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Which gift cards pay the highest rate online?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Amazon gift cards typically offer the highest payout rates (up to 93%), followed by Visa and Mastercard prepaid cards (up to 90%). Popular retail brands like Walmart, Target, and Best Buy also command strong rates between 82-88%. The exact rate depends on the card balance and current demand.
                </p>
              </div>

              {/* FAQ 4 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Can I sell partially used gift cards online?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! We accept both new and partially used gift cards. You'll receive a payout based on the remaining balance on your card. Simply enter the current card balance when submitting, and we'll provide an instant quote for that amount.
                </p>
              </div>

              {/* FAQ 5 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Do you accept digital gift cards or only physical cards?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We accept both physical and digital (e-gift) cards. For digital cards, simply upload a screenshot showing the card details. For physical cards, you can either upload photos of the front and back, or manually enter the card information. Both methods work equally well for instant gift card selling online.
                </p>
              </div>

              {/* FAQ 6 */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  What payment methods are available when I sell my gift cards?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We offer multiple convenient payout options: PayPal (most popular), Zelle, Cash App, Chime, and Bitcoin. All payment methods process same-day, so you can choose whichever is most convenient for you. No checks or mailed payments — everything is instant and digital.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-pink-600 to-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Award className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Sell Your Gift Cards Online?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join over 410,000 satisfied customers who have turned their unused gift cards into instant cash.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/form-submission">
                <button className="w-full sm:w-auto bg-white text-pink-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl transform inline-flex items-center gap-2">
                  Get Your Instant Quote
                  <ArrowRight className="w-5 h-5" />
                </button>
              </a>
              <a href="/rate-calculator">
                <button className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white hover:text-pink-600 px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 transform">
                  Check Card Rates
                </button>
              </a>
            </div>
            <p className="mt-8 text-sm opacity-75">
              Trusted since 2020 • 410,811+ customers served • A+ Rating
            </p>
          </div>
        </section>

        {/* SEO content — the full guide to selling gift cards online */}
        <section className="mt-16 max-w-4xl mx-auto px-6 bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">The complete guide to selling gift cards online in 2026</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Selling an unwanted gift card online used to involve clunky marketplaces, wary buyers, and
            unpredictable fees. Dedicated exchanges like CashifyGCMart cut all of that out. You submit the
            card, we verify the balance with the retailer, and we pay you directly — usually on the same
            day. Here's everything you need to know so the process feels obvious instead of mysterious.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Why sell online instead of at a kiosk?</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Physical kiosks in grocery stores typically pay 50–70% of the card value because they have
            overhead for machines, rent, and staff. Online exchanges run leaner, so rates land in the
            75–92% range for popular brands. You also get to choose your payout method and keep the full
            paper trail in case you need it later.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How much can you expect to earn?</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            As a rough benchmark: $100 Amazon or Walmart cards usually convert to $82–$87 cash. $100 Target
            cards are similar. Visa and Mastercard gift cards convert to around $90 because the next buyer
            can use them anywhere. Niche brands pay less — expect $55–$70 per $100 face value on regional
            or category-specific cards. The rate calculator shows the live number for your exact brand and
            balance.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Is it safe to sell gift cards online?</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Yes — with a reputable exchange. The three safety basics to check: the platform uses HTTPS end
            to end, publishes a physical address and real support contact, and pays you before you give up
            control of the card. CashifyGCMart does all three. We verify the card, confirm payment is
            queued, and only then do we deduct the balance for resale.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Common mistakes first-time sellers make</h3>
          <ul className="space-y-3 text-gray-700">
            <li>Entering the original face value instead of the remaining balance.</li>
            <li>Uploading dark or blurry photos — we need the full card number and PIN clearly visible.</li>
            <li>Submitting a card and then redeeming part of it before we verify — this creates a mismatch and slows payout.</li>
            <li>Choosing a payout method you don't actively use. Pick the one you check daily.</li>
            <li>Ignoring the confirmation email. The reference number inside is how you track your submission.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Taxes and reporting</h3>
          <p className="text-gray-700 leading-relaxed">
            For most sellers, converting an unwanted gift card to cash isn't a taxable event — you're just
            converting value you already owned. High-volume sellers may receive a 1099 at year-end if they
            cross reporting thresholds. If you're unsure how your activity is classified, check with a
            local tax professional. We're happy to provide a summary of your submissions on request.
          </p>
        </section>
      </div>
    </>
  );
};

export default SellGiftCardsOnline;
